import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiHome, FiUser, FiClipboard, FiCheckSquare,
    FiBarChart2, FiCalendar, FiBookOpen, FiCreditCard,
    FiFileText, FiLogOut, FiClock, FiDollarSign,
    FiShield, FiBriefcase, FiEdit2, FiEdit3, FiMenu,
    FiBook, FiSettings, FiPlus, FiChevronDown, FiChevronRight,
    FiAward, FiUsers, FiLayers, FiArchive, FiList, FiTruck,
    FiBell, FiSend
} from "react-icons/fi";

import { useApp } from "../context/AppContext";
import { CollegeSelector, YearSelector } from "../components/layout/Header";

// ─── Nav item component ───────────────────────────────────────────────────────
function PNavItem({ to, icon: Icon, label, end = false, subItem = false }) {
    return (
        <NavLink
            to={to}
            end={end}
            className={({ isActive }) =>
                `${subItem ? "nav-link nav-sublink" : "nav-link"} ${isActive ? "active" : ""}`
            }
        >
            {Icon && <Icon size={subItem ? 13 : 15} style={{ flexShrink: 0 }} />}
            {label}
        </NavLink>
    );
}

// ─── Nav group component (with collapsible sub-items) ────────────────────────
function PNavGroup({ icon: Icon, label, items, pathname }) {
    const isAnyActive = items.some((item) =>
        item.end ? pathname === item.to : pathname.startsWith(item.to)
    );
    const [isOpen, setIsOpen] = useState(isAnyActive);

    useEffect(() => {
        if (isAnyActive) {
            setIsOpen(true);
        }
    }, [isAnyActive]);

    return (
        <div className={`nav-group ${isAnyActive ? "active" : ""}`}>
            <button
                type="button"
                className={`nav-group-trigger ${isAnyActive ? "active" : ""}`}
                onClick={() => setIsOpen((prev) => !prev)}
            >
                <span style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                    {Icon && <Icon size={15} style={{ flexShrink: 0 }} />}
                    {label}
                </span>
                {isOpen ? (
                    <FiChevronDown size={13} style={{ flexShrink: 0, opacity: 0.7 }} />
                ) : (
                    <FiChevronRight size={13} style={{ flexShrink: 0, opacity: 0.7 }} />
                )}
            </button>
            {isOpen && (
                <div className="nav-submenu">
                    {items.map((sub) => (
                        <PNavItem key={sub.to} {...sub} subItem />
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Nav Configs (Declared first) ─────────────────────────────────────────────
const STUDENT_NAV = [
    { to: "/student", icon: FiHome, label: "Dashboard", end: true },
    { to: "/student/communication", icon: FiBell, label: "Communication" },
    { to: "/student/profile", icon: FiUser, label: "My Profile" },
    { to: "/student/attendance", icon: FiClipboard, label: "Attendance" },
    { to: "/student/results", icon: FiBarChart2, label: "Results" },
    { to: "/student/exams", icon: FiBookOpen, label: "Exams" },
    { to: "/student/timetable", icon: FiClock, label: "Timetable" },
    { to: "/student/homework", icon: FiEdit3, label: "Homework" },
    { to: "/student/fees", icon: FiCreditCard, label: "Fees" },
    { to: "/student/certificates", icon: FiFileText, label: "Certificates" },
    { to: "/student/events", icon: FiCalendar, label: "Events" },
];

const PARENT_NAV = [
    { to: "/parent", icon: FiHome, label: "Dashboard", end: true },
    { to: "/parent/communication", icon: FiBell, label: "Communication" },
    { to: "/parent/profile", icon: FiUser, label: "Child Profile" },
    { to: "/parent/attendance", icon: FiClipboard, label: "Attendance" },
    { to: "/parent/results", icon: FiBarChart2, label: "Results" },
    { to: "/parent/exams", icon: FiBookOpen, label: "Exams" },
    { to: "/parent/timetable", icon: FiClock, label: "Timetable" },
    { to: "/parent/homework", icon: FiEdit3, label: "Homework" },
    { to: "/parent/fees", icon: FiCreditCard, label: "Fees" },
    { to: "/parent/certificates", icon: FiFileText, label: "Certificates" },
    { to: "/parent/events", icon: FiCalendar, label: "Events" },
];

const TEACHER_NAV = [
    { to: "/teacher", icon: FiHome, label: "Dashboard", end: true },
    { to: "/teacher/communication", icon: FiSend, label: "Communication" },
    { to: "/teacher/profile", icon: FiBriefcase, label: "My Profile" },
    { to: "/teacher/leave", icon: FiCalendar, label: "My Leaves" },
    { to: "/teacher/attendance", icon: FiClipboard, label: "My Attendance" },
    { to: "/teacher/mark-attendance", icon: FiCheckSquare, label: "Mark Attendance" },
    { to: "/teacher/payslips", icon: FiDollarSign, label: "Payslips" },
    { to: "/teacher/subjects", icon: FiBookOpen, label: "My Subjects" },
    { to: "/teacher/syllabus", icon: FiLayers, label: "Syllabus Tracker" },
    { to: "/teacher/homework", icon: FiEdit3, label: "Homework" },
    { to: "/teacher/exams", icon: FiEdit2, label: "Exams & Results" },
    { to: "/teacher/events", icon: FiCalendar, label: "Events" },
];


const LIBRARIAN_NAV = [
    { to: "/librarian", icon: FiHome, label: "Dashboard", end: true },
    { to: "/librarian/books", icon: FiBook, label: "Book Catalog" },
    { to: "/librarian/books/add", icon: FiEdit2, label: "Add Book" },
    { to: "/librarian/issues", icon: FiClipboard, label: "Issue / Return" },
    { to: "/librarian/fines", icon: FiDollarSign, label: "Pending Fines" },
    { to: "/librarian/settings", icon: FiSettings, label: "Library Settings" },
];

const ACCOUNTANT_NAV = [
    { to: "/accountant", icon: FiHome, label: "Dashboard", end: true },
    { to: "/accountant/fees", icon: FiCreditCard, label: "Fee Dashboard" },
    { to: "/accountant/fees/receipts", icon: FiFileText, label: "Fee Receipts" },
    { to: "/accountant/fees/new", icon: FiPlus, label: "Collect Fees" },
    { to: "/accountant/fees/pending", icon: FiDollarSign, label: "Pending Fees" },
    { to: "/accountant/fines", icon: FiShield, label: "Charges & Fines" },
    { to: "/accountant/transport", icon: FiTruck, label: "Bus / Transport" },
    { to: "/accountant/reconciliation", icon: FiCheckSquare, label: "Cash & Bank Reconciliation" },
    { to: "/accountant/fees/monthly", icon: FiBarChart2, label: "Monthly Reports" },
    { to: "/accountant/payroll", icon: FiBriefcase, label: "Payroll" },
];

const WARDEN_NAV = [
    { to: "/warden", icon: FiHome, label: "Dashboard", end: true },
    { to: "/warden/hostels", icon: FiHome, label: "Hostels" },
    { to: "/warden/allocations", icon: FiClipboard, label: "Allocations" },
    { to: "/warden/fee-collect", icon: FiCreditCard, label: "Collect Fees" },
    { to: "/warden/fee-receipts", icon: FiFileText, label: "Hostel Receipts" },
    { to: "/warden/pending-fees", icon: FiDollarSign, label: "Pending Dues" },
];

const CLERK_NAV = [
    { to: "/clerk", icon: FiHome, label: "Dashboard", end: true },
    { to: "/clerk/students", icon: FiUsers, label: "Students" },
    { to: "/clerk/admissions", icon: FiBriefcase, label: "Admission" },
    {
        label: "Certificates",
        icon: FiAward,
        items: [
            { to: "/clerk/bonafide", icon: FiFileText, label: "Bonafide" },
            { to: "/clerk/leaving", icon: FiFileText, label: "Leaving" },
        ],
    },
    {
        label: "Fees Management",
        icon: FiCreditCard,
        items: [
            { to: "/clerk/fees", icon: FiCreditCard, label: "Fees Dashboard", end: true },
            { to: "/clerk/fees/receipts", icon: FiFileText, label: "Fees Receipts" },
            { to: "/clerk/fees/new", icon: FiPlus, label: "Collect Fees" },
            { to: "/clerk/fees/pending", icon: FiDollarSign, label: "Pending Fees" },
            { to: "/clerk/fines", icon: FiShield, label: "Charges & Fines" },
            { to: "/clerk/transport", icon: FiTruck, label: "Bus / Transport" },
            { to: "/clerk/cash-handover", icon: FiDollarSign, label: "Cash Handover Desk" },
            { to: "/clerk/fees/monthly", icon: FiBarChart2, label: "Monthly Reports" },
        ],
    },
];
const STOREKEEPER_NAV = [
    { to: "/storekeeper", icon: FiHome, label: "Dashboard", end: true },
    { to: "/storekeeper/inventory/items", icon: FiArchive, label: "Item Master" },
    { to: "/storekeeper/inventory/items/new", icon: FiEdit2, label: "Add Item" },
    { to: "/storekeeper/inventory/transactions", icon: FiList, label: "Stock Transactions" },
    { to: "/storekeeper/inventory/categories", icon: FiLayers, label: "Categories" },
    { to: "/storekeeper/inventory/suppliers", icon: FiTruck, label: "Suppliers" },
];

// ─── Role Config Mapping (Must be defined before component) ───────────────────
const ROLE_CONFIG = {
    student: { nav: STUDENT_NAV, color: "#003b67", label: "Student Portal", badge: "Student" },
    parent: { nav: PARENT_NAV, color: "#059669", label: "Parent Portal", badge: "Parent" },
    teacher: { nav: TEACHER_NAV, color: "#7c3aed", label: "Teacher Portal", badge: "Teacher" },
    librarian: { nav: LIBRARIAN_NAV, color: "#0284c7", label: "Librarian Portal", badge: "Librarian" },
    accountant: { nav: ACCOUNTANT_NAV, color: "#059669", label: "Accounts Portal", badge: "Accountant" },
    warden: { nav: WARDEN_NAV, color: "#d97706", label: "Hostel Portal", badge: "Warden" },
    clerk: { nav: CLERK_NAV, color: "#4b5563", label: "Admin Clerk Portal", badge: "Clerk" },
    storekeeper: { nav: STOREKEEPER_NAV, color: "#ea580c", label: "Storekeeper Portal", badge: "Storekeeper" },
};

// ─── Layout Component ─────────────────────────────────────────────────────────
export default function PortalLayout({ children }) {
    const {
        user, role, logout,
        academicYear, financialYear,
        academicYears, financialYears,
        selectAcademicYear, selectFinancialYear,
        collegeData, allColleges, selectedCollegeId, selectCollege,
        collegeName, logoSrc,
    } = useApp();
    const navigate = useNavigate();
    const location = useLocation();

    const [sidebarOpen, setSidebarOpen] = useState(false);

    useEffect(() => {
        setSidebarOpen(false);
    }, [location.pathname]);

    const cfg = ROLE_CONFIG[role] || ROLE_CONFIG.student;

    const handleLogout = () => {
        logout();
        toast.success("Logged out successfully.");
        navigate("/login", { replace: true });
    };

    const initials = (user?.displayName || user?.username || "?")
        .split(" ").filter(Boolean).slice(0, 2)
        .map((p) => p[0]?.toUpperCase()).join("");

    return (
        <div className="app-layout">
            {sidebarOpen && (
                <div
                    className="sidebar-overlay"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            <aside className={`sidebar ${sidebarOpen ? "open" : ""}`}>
                <div className="sidebar-logo" style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: "8px", padding: "1.2rem 1rem" }}>
                    <div style={{
                        width: 96,
                        height: 96,
                        borderRadius: "12px",
                        background: "rgba(255, 255, 255, 0.08)",
                        border: "1px solid rgba(255, 255, 255, 0.12)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "4px",
                        overflow: "hidden",
                    }}>
                        <img
                            src={logoSrc}
                            alt={collegeName}
                            style={{ width: "100%", height: "100%", objectFit: "contain" }}
                        />
                    </div>
                    <div>
                        <h2 style={{ fontSize: "13px", fontWeight: 700, color: "#f8fafc", lineHeight: 1.25, margin: 0 }}>
                            {selectedCollegeId === "all" && (role === "accountant" || role === "superadmin")
                                ? "All Campuses (Group)"
                                : collegeName}
                        </h2>
                        <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: 4, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                            {cfg.label}
                        </div>
                    </div>
                </div>

                <nav className="sidebar-nav" style={{ flex: 1 }}>
                    {cfg.nav.map((item) =>
                        item.items ? (
                            <PNavGroup
                                key={item.label}
                                label={item.label}
                                icon={item.icon}
                                items={item.items}
                                pathname={location.pathname}
                            />
                        ) : (
                            <PNavItem key={item.to} {...item} />
                        )
                    )}
                </nav>

                <div style={{ padding: "0.75rem 1rem", borderTop: "1px solid #334155" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.6rem" }}>
                        <div style={{ width: 32, height: 32, borderRadius: "50%", background: cfg.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                            {initials}
                        </div>
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#f1f5f9", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {user?.displayName || user?.username}
                            </div>
                            <div style={{ fontSize: 10.5 }}>
                                <span style={{ background: cfg.color + "30", color: cfg.color, padding: "1px 5px", borderRadius: 99, fontWeight: 600 }}>
                                    {cfg.badge}
                                </span>
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={handleLogout}
                        style={{ width: "100%", display: "flex", alignItems: "center", gap: "0.4rem", background: "transparent", border: "1px solid #475569", color: "#94a3b8", borderRadius: 6, padding: "5px 10px", fontSize: 12, cursor: "pointer", justifyContent: "center", transition: "all 0.15s" }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "#dc2626"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#dc2626"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#94a3b8"; e.currentTarget.style.borderColor = "#475569"; }}
                    >
                        <FiLogOut size={13} /> Logout
                    </button>
                </div>
            </aside>

            <div className="content-wrapper">
                <header className="main-header">
                    <button
                        className="hamburger-btn"
                        onClick={() => setSidebarOpen((p) => !p)}
                        title="Toggle menu"
                    >
                        <FiMenu size={20} />
                    </button>

                    <span className="header-title" style={{ display: "inline-block" }}>
                        {cfg.label}
                    </span>

                    <div className="header-right">
                        {/* College Selector for Accountant & Super Admin */}
                        {(role === "accountant" || role === "superadmin") && (
                            <CollegeSelector
                                label="Campus"
                                value={selectedCollegeId === "all" ? "All Campuses" : (collegeData?.name || "All Campuses")}
                                colleges={allColleges}
                                onSelect={selectCollege}
                            />
                        )}

                        {/* Academic Year */}
                        <YearSelector
                            icon={FiCalendar}
                            iconColor="#003b67"
                            label="Academic Year"
                            value={academicYear?.label}
                            years={academicYears}
                            onSelect={selectAcademicYear}
                        />

                        {/* Financial Year for accounts / clerk / warden roles */}
                        {["admin", "accountant", "clerk", "warden"].includes(role) && (
                            <YearSelector
                                icon={FiDollarSign}
                                iconColor="#16a34a"
                                label="Financial Year"
                                value={financialYear?.label}
                                years={financialYears}
                                onSelect={selectFinancialYear}
                            />
                        )}

                        {/* Divider */}
                        <div style={{ width: 1, height: 24, background: "var(--border)", flexShrink: 0 }} />

                        {/* User badge */}
                        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: 13, color: "var(--text)" }}>
                            <div style={{ width: 28, height: 28, borderRadius: "50%", background: cfg.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                                {initials}
                            </div>
                            <span style={{ fontWeight: 600, whiteSpace: "nowrap" }} className="header-username">
                                {user?.displayName || user?.username || "User"}
                            </span>
                        </div>

                        {/* Logout */}
                        <button
                            onClick={handleLogout}
                            style={{
                                display: "flex", alignItems: "center", gap: "0.35rem",
                                background: "transparent", border: "1px solid var(--border)",
                                color: "var(--text-muted)", borderRadius: 8,
                                padding: "5px 10px", fontSize: 12.5, cursor: "pointer",
                                transition: "all 0.15s", whiteSpace: "nowrap",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = "#dc2626"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#dc2626"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--text-muted)"; e.currentTarget.style.borderColor = "var(--border)"; }}
                        >
                            <FiLogOut size={13} />
                            <span className="logout-label">Logout</span>
                        </button>
                    </div>
                </header>

                <main className="main-content">
                    {children}
                </main>
            </div>
        </div>
    );
}